import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  POD_SECURITY_STANDARDS_INITIAL_VALUES,
  PodSecurityNamespaceEntry,
  PodSecurityStandardsFormValues,
  PodSecurityStandardsPolicyPayload,
} from './types';

export function usePodSecurityStandardsInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): PodSecurityStandardsFormValues {
  return useMemo(
    () => getInitialValuesFromPolicy(policy, templateData),
    [policy, templateData]
  );
}

export function getInitialValuesFromPolicy(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): PodSecurityStandardsFormValues {
  // Create mode: start from template namespaces when a template is selected.
  if (!policy) {
    if (isPodSecurityStandardsPayload(templateData)) {
      return {
        ...POD_SECURITY_STANDARDS_INITIAL_VALUES,
        namespaces: normalizeNamespaces(templateData.namespaces),
      };
    }
    return POD_SECURITY_STANDARDS_INITIAL_VALUES;
  }

  const base = {
    name: policy.Name || '',
    environmentGroupIds: policy.EnvironmentGroups || [],
  };

  if (!isPodSecurityStandardsPayload(policy.Data)) {
    return { ...POD_SECURITY_STANDARDS_INITIAL_VALUES, ...base };
  }

  return { ...base, namespaces: normalizeNamespaces(policy.Data.namespaces) };
}

function normalizeNamespaces(
  namespaces: Array<PodSecurityNamespaceEntry>
): Array<PodSecurityNamespaceEntry> {
  return namespaces.map((entry) => ({
    namespace: entry.namespace,
    enforce: entry.enforce,
    audit: entry.audit,
    warn: entry.warn,
  }));
}

function isPodSecurityStandardsPayload(
  data: unknown
): data is PodSecurityStandardsPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'namespaces' in data &&
    Array.isArray((data as PodSecurityStandardsPolicyPayload).namespaces)
  );
}
